import { Component, inject, OnInit } from '@angular/core';
import { AuthenticationService, HomePageService, WelcomeContentViewModel } from 'src/app/core';
import { ShowMoreHtmlComponent } from '@shared/component/show-more-html/show-more-html.component';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { AccordionModule } from 'primeng/accordion';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

@Component({
  selector: 'app-welcome-content',
  imports: [
    ShowMoreHtmlComponent,
    FormsModule,
    CheckboxModule,
    AccordionModule,
    ProgressSpinnerModule
  ],
  templateUrl: './welcome-content.component.html',
  styleUrls: ['./welcome-content.component.scss']
})
export class WelcomeContentComponent implements OnInit {
  homeService = inject(HomePageService);
  authenticationService = inject(AuthenticationService);

  isLoading = true;
  userName = '';
  welcomeContent: WelcomeContentViewModel[] = [];

  // Accordion state
  alertActivePanels: Record<number, string[]> = {};
  itemActivePanels: Record<number, number[]> = {};

  // Per-section content lists, precomputed so the template does not
  // re-filter/re-sort on every change detection cycle
  alertContent: Record<number, any[]> = {};
  nonAlertContent: Record<number, any[]> = {};

  ngOnInit(): void {
    this.userName = `${this.authenticationService.claims.given_name} ${this.authenticationService.claims.family_name}`;

    this.getWelcomeContent();
  }

  async getWelcomeContent(): Promise<void> {
    this.isLoading = true;

    const res = await this.homeService.getWelcomeContent().toPromise();

    this.welcomeContent = res ?? [];
    this.alertContent = {};
    this.nonAlertContent = {};
    this.welcomeContent.forEach((section: any, i: number) => {
      section.isAllAttested = this.getAllAttested(section);

      // Auto open Alerts if not fully attested
      if (!section.isAllAttested || section.collapseAlert) {
        this.alertActivePanels[i] = ['alerts'];
      }

      // Auto open individual alerts
      const alerts = this.getAlertContent(section);
      this.alertContent[i] = alerts;
      this.nonAlertContent[i] = this.getNonAlertContent(section);

      this.itemActivePanels[i] = [];

      alerts.forEach((wc: any) => {
        if (!wc.isAttested || wc.collapseAttestedAlert) {
          this.itemActivePanels[i].push(wc.welcomeContentId);
        }
      });
    });

    this.isLoading = false;
  }

  getAlertContent(content: any): any[] {
    const critical = content.welcomeContent.filter((x: any) =>
      x.contentTypeName === 'Alerts' &&
      x.isCritical &&
      x.isAttested === false
    );

    const nonCritical = content.welcomeContent.filter((x: any) =>
      x.contentTypeName === 'Alerts' &&
      !x.isCritical &&
      x.isAttested === false
    );

    const criticalAtt = content.welcomeContent.filter((x: any) =>
      x.contentTypeName === 'Alerts' &&
      x.isCritical &&
      x.isAttested === true
    );

    const nonCriticalAtt = content.welcomeContent.filter((x: any) =>
      x.contentTypeName === 'Alerts' &&
      !x.isCritical &&
      x.isAttested === true
    );

    const sortByDate = (a: any, b: any) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime();

    return [
      ...critical.sort(sortByDate),
      ...nonCritical.sort(sortByDate),
      ...criticalAtt.sort(sortByDate),
      ...nonCriticalAtt.sort(sortByDate)
    ];
  }

  getAllAttested(content: any): boolean {
    return !content.welcomeContent.find((x: any) => x.contentTypeName === 'Alerts' && !x.isAttested);
  }

  getNonAlertContent(content: any): any[] {
    return content.welcomeContent.filter((x: any) => x.contentTypeName !== 'Alerts');
  }

  onCheckboxClick(event: any, welcomeContent: any, content: any, sectionIndex: number): void {
    welcomeContent.isAttested = event.checked;
    welcomeContent.collapseAttestedAlert = true;
    this.alertContent[sectionIndex] = this.getAlertContent(content);

    this.homeService
      .attestWelcomeContent(welcomeContent.welcomeContentId)
      .subscribe({
        next: () => {
          content.isAllAttested = this.getAllAttested(content);

          // remove from open list
          this.itemActivePanels[sectionIndex] =
            (this.itemActivePanels[sectionIndex] ?? [])
              .filter(id => id !== welcomeContent.welcomeContentId);

          // collapse alerts if all attested
          if (content.isAllAttested) {
            content.collapseAlert = true;
            this.alertActivePanels[sectionIndex] = [];
          }
        }
      });
  }
}
